import { useState } from 'react';
import {
  Pill,
  Heart,
  Stethoscope,
  CalendarClock,
  ExternalLink,
  ArrowRight,
  Loader2,
  ShoppingCart,
  Package,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useProductSearch, useCheckout } from '@/api/hooks/useFullscript';
import { api } from '@/api/client';
import type { Recommendation } from '@/api/hooks/useBloodwork';
import type { FullscriptProduct } from '@/lib/fullscript';

interface RecommendationCardProps {
  rec: Recommendation;
}

const typeConfig = {
  supplement: {
    icon: Pill,
    accent: 'border-l-copper',
    label: 'Supplement',
    bg: 'bg-orange-50',
  },
  lifestyle: {
    icon: Heart,
    accent: 'border-l-emerald-500',
    label: 'Lifestyle',
    bg: 'bg-emerald-50',
  },
  clinical: {
    icon: Stethoscope,
    accent: 'border-l-red-500',
    label: 'Clinical',
    bg: 'bg-red-50',
  },
  retest: {
    icon: CalendarClock,
    accent: 'border-l-slate-brand',
    label: 'Retest',
    bg: 'bg-blue-50',
  },
};

function PriorityDots({ priority }: { priority: number }) {
  const filled = Math.max(1, Math.min(5, 6 - priority));
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${
            i <= filled ? 'bg-copper' : 'bg-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

function ProductSearchModal({
  open,
  onClose,
  searchTerms,
  dosageGuidance,
  recId,
}: {
  open: boolean;
  onClose: () => void;
  searchTerms: string;
  dosageGuidance: string;
  recId: string;
}) {
  const { data: products, isLoading, error } = useProductSearch(open ? searchTerms : '');
  const checkout = useCheckout();
  const [selectedProduct, setSelectedProduct] = useState<FullscriptProduct | null>(null);

  const handleCheckout = async (product: FullscriptProduct) => {
    setSelectedProduct(product);

    // Track click
    try {
      await api.post(`/recommendations/${recId}/click`, {});
    } catch {
      // Non-blocking
    }

    checkout.mutate(
      [
        {
          productId: product.id,
          variantId: product.variantId,
          quantity: 1,
          dosageInstructions: dosageGuidance,
        },
      ],
      {
        onSuccess: (result) => {
          // Open checkout in new window
          window.open(result.checkoutUrl, '_blank', 'noopener,noreferrer');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-copper" />
            Select Product
          </DialogTitle>
          <DialogDescription>
            Choose from verified supplement options below.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 max-h-[400px] space-y-3 overflow-y-auto pr-1">
          {isLoading && (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 rounded-lg border border-gray-100 p-3">
                  <Skeleton className="h-16 w-16 shrink-0 rounded-md" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              ))}
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Unable to search products right now. Please try again later.
            </div>
          )}

          {!isLoading && !error && products && products.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-gray-500">
              <Package className="h-8 w-8 text-gray-300" />
              <p>No matching products found.</p>
              <p className="text-xs">This product may not be available through our supplement partner yet.</p>
            </div>
          )}

          {!isLoading &&
            products?.map((product) => (
              <div
                key={product.id}
                className="flex gap-3 rounded-lg border border-gray-100 p-3 transition-colors hover:border-copper/30 hover:bg-orange-50/30"
              >
                {/* Product image */}
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-16 w-16 shrink-0 rounded-md bg-gray-50 object-contain"
                  />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-gray-50">
                    <Pill className="h-6 w-6 text-gray-300" />
                  </div>
                )}

                {/* Product info */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-navy leading-tight">
                    {product.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-400">{product.brand}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    {product.price > 0 && (
                      <span className="text-sm font-semibold text-navy">
                        ${product.price.toFixed(2)}
                      </span>
                    )}
                    {!product.inStock && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                        Out of stock
                      </span>
                    )}
                  </div>

                  <Button
                    size="sm"
                    className="mt-2 h-7 text-xs"
                    disabled={
                      !product.inStock ||
                      (checkout.isPending && selectedProduct?.id === product.id)
                    }
                    onClick={() => handleCheckout(product)}
                  >
                    {checkout.isPending && selectedProduct?.id === product.id ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        Creating plan...
                      </>
                    ) : (
                      <>
                        Add to Plan & Checkout
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
        </div>

        {checkout.isError && (
          <div className="mt-2 rounded-lg bg-red-50 p-3 text-xs text-red-600">
            Failed to create checkout. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RecommendationCard({ rec }: RecommendationCardProps) {
  const config = typeConfig[rec.type] || typeConfig.lifestyle;
  const Icon = config.icon;
  const [showProductModal, setShowProductModal] = useState(false);

  // Extract dosage guidance from the reason text (after the last period before guidance)
  const dosageMatch = rec.reason?.match(/\.\s*([^.]*daily[^.]*|[^.]*before bed[^.]*|[^.]*with (?:food|meals)[^.]*)/i);
  const dosageGuidance = dosageMatch ? dosageMatch[1].trim() : '';

  return (
    <>
      <Card className={`border-l-4 ${config.accent}`}>
        <CardContent>
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${config.bg}`}
            >
              <Icon className="h-4.5 w-4.5 text-gray-600" />
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {config.label}
                </span>
                <PriorityDots priority={rec.priority} />
              </div>

              {/* Supplement type */}
              {rec.type === 'supplement' && (
                <>
                  {rec.product_name && (
                    <h4 className="mb-1 text-sm font-semibold text-navy">
                      {rec.product_name}
                    </h4>
                  )}
                  {rec.price_range && (
                    <span className="mb-1 inline-block text-xs text-gray-400">
                      {rec.price_range}
                    </span>
                  )}
                  <p className="mb-3 text-sm leading-relaxed text-gray-600">
                    {rec.reason}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {rec.pmids?.length > 0 && (
                      <a
                        href={`https://mensresearch.ai/research/${rec.pmids[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-slate-brand hover:underline"
                      >
                        View Research
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {rec.product_name && (
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setShowProductModal(true)}
                      >
                        Get This
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Lifestyle type */}
              {rec.type === 'lifestyle' && (
                <p className="text-sm leading-relaxed text-gray-600">
                  {rec.reason}
                </p>
              )}

              {/* Clinical type */}
              {rec.type === 'clinical' && (
                <>
                  {rec.product_name && rec.product_name !== 'Consult Healthcare Provider' && (
                    <h4 className="mb-1 text-sm font-semibold text-navy">
                      {rec.product_name}
                    </h4>
                  )}
                  <p className="mb-3 text-sm leading-relaxed text-gray-600">
                    {rec.reason}
                  </p>
                  <Button variant="outline" size="sm" className="h-7 text-xs">
                    Speak with a Licensed Provider
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </>
              )}

              {/* Retest type */}
              {rec.type === 'retest' && (
                <>
                  <p className="text-sm leading-relaxed text-gray-600">
                    {rec.reason}
                  </p>
                  {rec.retest_timeline && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-brand/10 px-3 py-1 text-xs font-medium text-slate-brand">
                      <CalendarClock className="h-3 w-3" />
                      Suggested: {rec.retest_timeline}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Product search + checkout modal */}
      {rec.type === 'supplement' && rec.product_name && (
        <ProductSearchModal
          open={showProductModal}
          onClose={() => setShowProductModal(false)}
          searchTerms={rec.product_name}
          dosageGuidance={dosageGuidance}
          recId={rec.id}
        />
      )}
    </>
  );
}
